<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { onMounted, ref, type Ref } from "vue";
import {
  formatBuildCommit,
  loadSystemSource,
  loadSystemVersion,
  type SystemSource,
  type SystemVersion,
} from "../api/system-information.js";

const currentYear: number = new Date().getUTCFullYear();
const productVersion: Ref<string | null> = ref(null);
const gitCommit: Ref<string | null> = ref(null);
const source: Ref<SystemSource | null> = ref(null);

async function loadFooterInformation(): Promise<void> {
  const results: PromiseSettledResult<SystemVersion | SystemSource>[] = await Promise.allSettled([
    loadSystemVersion(),
    loadSystemSource(),
  ]);
  const versionResult: PromiseSettledResult<SystemVersion | SystemSource> | undefined = results[0];
  const sourceResult: PromiseSettledResult<SystemVersion | SystemSource> | undefined = results[1];
  if (versionResult?.status === "fulfilled" && "productVersion" in versionResult.value) {
    productVersion.value = versionResult.value.productVersion;
    gitCommit.value = formatBuildCommit(versionResult.value.gitCommit);
  }
  if (sourceResult?.status === "fulfilled" && "repositoryUrl" in sourceResult.value) {
    source.value = sourceResult.value;
  }
}

onMounted(loadFooterInformation);
</script>

<template>
  <footer class="app-footer">
    <div class="app-footer__links">
      <span>© {{ currentYear }} LacunaScope</span>
      <a
        href="https://www.gnu.org/licenses/agpl-3.0.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ source?.license ?? "AGPL-3.0-only" }} license
      </a>
      <a
        v-if="source"
        :href="source.repositoryUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        Corresponding source
      </a>
      <RouterLink :to="{ name: 'privacy' }">Privacy policy</RouterLink>
    </div>
    <span class="app-footer__version">
      Version {{ productVersion ?? "unavailable" }} ({{ gitCommit ?? "unknown" }})
    </span>
  </footer>
</template>
